<template>
  <div class="container pt-3">
    <div class="mb-6 has-background-primary-light rounded">
      <div class="hero-body pt-6 px-7">
        <div
          class="header has-text-centered background-style"
          :style="{
            backgroundImage: `url(${require('@/assets/aboutus-background.jpg')})`,
            backgroundPosition: '50%',
            backgroundRepeat: 'no-repeat',
          }"
        >
          <h1 class="title is-4 has-text-dark-gray">
            <span class="title-style">Capturing Joy</span> in Every Stay.<br />
            Transforming Properties into Memorable Experiences
          </h1>
          <p class="is-size-6 mt-1 has-text-dark-gray mb-6">
            We specialize in transforming properties into joyful experiences for
            both homeowners and guests. <br />From seamless Airbnb management to
            property makeovers and maintenance, we handle everything to ensure
            every moment sparks happiness.
          </p>

          <div class="columns is-centered is-multiline">
            <!-- First Card -->
            <div class="column is-6-desktop is-12-touch">
              <div class="card accented-border-radius glassy-card rounded">
                <div class="card-content">
                  <div class="has-text-centered pricing-header rounded mb-4">
                    <b-image
                      :src="require('@/assets/owner-banner.png')"
                      ratio="601by235"
                    ></b-image>
                  </div>
                  <div
                    class="has-background-white pricing-card-content has-text-left"
                  >
                    <div class="is-size-5 has-text-dark-gray mb-1">
                      For Owners
                    </div>
                    <div class="pricing">
                      <span class="title is-4">30 - 100%</span>
                      <span class="has-text-gray-3 is-size-6 ml-2"
                        >more income / year</span
                      >
                    </div>
                    <p class="has-text-gray-2 mt-5">
                      We maximize your property’s potential through expert
                      renovations, top-notch management, and tech-driven
                      insights.
                    </p>
                    <a
                      href="/contact-us"
                      class="button is-primary is-fullwidth mt-5"
                      >Contact Us</a
                    >

                    <ul class="mt-5 is-size-6">
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Maximized income with dynamic pricing
                        </p>
                      </li>
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Hassle-free management and timely support
                        </p>
                      </li>
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Guaranteed monthly payments
                        </p>
                      </li>
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Professional maintenance and cleaning
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Second Card -->
            <div class="column is-6-desktop is-12-touch">
              <div class="card accented-border-radius glassy-card rounded">
                <div class="card-content">
                  <div class="has-text-centered pricing-header mb-4">
                    <b-image
                      :src="require('@/assets/guest-banner.png')"
                      ratio="601by235"
                    ></b-image>
                  </div>
                  <div
                    class="has-background-white pricing-card-content has-text-left"
                  >
                    <div class="is-size-5 has-text-dark-gray mb-1">
                      For Guests
                    </div>
                    <div class="pricing">
                      <span class="title is-4"
                        >Clean, comfortable, and convenient
                      </span>
                    </div>
                    <p class="has-text-gray-2 mt-5">
                      We create warm, welcoming stays designed to feel like home
                      — with thoughtful touches that leave lasting impressions.
                    </p>
                    <a
                      href="/homestays"
                      class="button is-primary is-light is-outlined is-fullwidth mt-5"
                      >View Homestays</a
                    >

                    <ul class="mt-5 is-size-6">
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Flexible booking options
                        </p>
                      </li>
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Great stay experiences
                        </p>
                      </li>
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Seamless check-in/check-out
                        </p>
                      </li>
                      <li>
                        <p>
                          <b-icon
                            icon="check"
                            class="has-text-success pr-2"
                            size="is-small"
                          ></b-icon
                          >Access to exclusive deals and services
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-6 p-5">
      <h1 class="has-text-centered title is-3 has-text-dark-gray mb-5">
        Why Choose Us
      </h1>

      <div class="container mb-6">
        <div class="columns is-multiline">
          <div
            class="column is-one-third-desktop is-full-mobile is-half-tablet"
            v-for="(reason, index) in reasons"
            :key="index"
          >
            <div
              class="why-us-card rounded has-background-light p-5"
              style="height: 100%"
            >
              <b-image
                :src="require(`@/assets/${reason.img}`)"
                :alt="reason.alt"
                style="width: 100px"
                class="mb-4"
              />
              <label v-if="reason.label">
                <span class="top">{{ reason.labelTop }}</span>
                <span class="major">{{ reason.labelMajor }}</span>
                <span>{{ reason.labelMinor }}</span>
              </label>
              <h3 class="title is-6">{{ reason.title }}</h3>
              <p class="is-7">{{ reason.description }}</p>
            </div>
          </div>
        </div>
      </div>
      <h1 class="has-text-centered title is-3 has-text-dark-gray mb-5">
        Our Team
      </h1>
      <div class="columns is-multiline">
        <!-- Part 1 -->
        <div class="column is-one-third-desktop is-half-tablet is-full-mobile">
          <div class="has-text-centered">
            <figure class="image is-128x128 is-inline-block">
              <b-image class="" :src="require(`@/assets/jack.png`)" />
            </figure>
            <div class="is-size-4 has-text-weight-semibold">Jack</div>
            <div class="is-size-6">Director</div>
            <div class="speech-bubble">
              <p>
                We combine creativity, innovation, and hospitality to turn
                ordinary spaces into extraordinary experiences.
              </p>
            </div>
          </div>
        </div>

        <!-- Part 2 -->
        <div class="column is-one-third-desktop is-half-tablet is-full-mobile">
          <div class="has-text-centered">
            <figure class="image is-128x128 is-inline-block">
              <b-image class="" :src="require(`@/assets/chris.png`)" />
            </figure>
            <div class="is-size-4 has-text-weight-semibold">Chris</div>
            <div class="is-size-6">Operation</div>
            <div class="speech-bubble">
              <p>
                With every property we manage, our mission is simple: to capture
                and share the joy of a perfect stay.
              </p>
            </div>
          </div>
        </div>

        <!-- Part 3 -->
        <div
          class="column is-one-third-desktop is-half-tablet is-full-mobile"
          style="height: 100%"
        >
          <div class="has-text-centered" style="height: 100%">
            <figure class="image is-128x128 is-inline-block">
              <b-image class="" :src="require(`@/assets/des.png`)" />
            </figure>
            <div class="is-size-4 has-text-weight-semibold">Desmond</div>
            <div class="is-size-6">Tech & Creative</div>
            <div class="speech-bubble" style="height: 100%">
              <p>
                Let us help you unlock the true potential of your property with
                our AI-empowered solutions.
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- <div class="has-text-centered is-flex is-justify-content-center mb-5">
        <b-image
          class="illustration-about"
          :src="require(`@/assets/undraw/joy.svg`)"
        />
      </div>
      <div
        class="has-text-left has-text-weight-medium m-auto"
        style="max-width: 600px"
      >
        <b-message type="is-primary">
          We combine creativity, innovation, and hospitality to turn ordinary
          spaces into extraordinary experiences. <br /><br />With every property
          we manage, our mission is simple: to capture and share the joy of a
          perfect stay. <br /><br />Let us help you unlock the true potential of
          your property.</b-message
        >
      </div> -->
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      reasons: [
        {
          img: "whyus-1.svg",
          alt: "Icon of house featuring budget-friendly makeovers",
          labelTop: "save",
          labelMajor: "70",
          labelMinor: "%",
          title: "Budget-Friendly Makeovers",
          description:
            "We spend only on essential materials in order to provide you with an affordable designer home.",
        },
        {
          img: "whyus-2.svg",
          alt: "Icon of hand and clock featuring we work fast",
          labelMajor: "30",
          labelMinor: "days",
          title: "We Work Fast",
          description:
            "Your property will be ready to rent in just 30 working days!",
        },
        {
          img: "whyus-3.svg",
          alt: "Icon of house featuring high rental rate",
          labelMajor: "99",
          labelMinor: "%",
          title: "High Rental Rate",
          description:
            "99% of our units are successfully rented out at top tier rates!",
        },
        {
          img: "whyus-4.svg",
          alt: "Icon of hidden cost",
          title: "No Hidden Cost",
          description:
            "Our team will walk you through a fully-transparent cost breakdown.",
        },
        {
          img: "whyus-5.svg",
          alt: "Icon of warranty",
          title: "12 Months Warranty",
          description:
            "We guarantee a job well done so you can welcome new tenants with open arms.",
        },
        {
          img: "whyus-6.svg",
          alt: "Icon of hidden cost",
          title: "Worry Free",
          description:
            "Just pass us the keys, we'll do what we do best while you prepare to collect rent!",
        },
      ],
    };
  },
};
</script>

<style>
.title-style {
  display: inline-block;
  background: linear-gradient(28deg, #9aa2ff, #a7f7e5);
  background-size: 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-animation: Gradient-data-v-332e75c9 3s ease infinite;
  animation: Gradient-data-v-332e75c9 3s ease infinite;
}

.background-style {
  background-position-y: 340px;
  background-size: 60%;
  image-rendering: -moz-crisp-edges;
  image-rendering: -o-crisp-edges;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  -ms-interpolation-mode: nearest-neighbor;
}

img {
  border-radius: 0.5rem;
}

.illustration-about {
  img {
    height: 200px !important;
    width: auto;
  }
}

.media-content {
  color: #3b3b3b;
}

.top {
  font-size: 0.9em;
  display: block;
}
.major {
  font-size: 2em;
  font-weight: bold;
}

.why-us-card:hover {
  background: #edf1fd !important;
}

.speech-bubble {
  height: 100%;
  /* background-color: #f0f0f0; */
  border: 1px solid #d1d1d1;
  border-radius: 10px;
  padding: 1rem;
  margin-top: 10px;
  display: inline-block;
  position: relative;
}
</style>
